/**
 * Fold every decimal digit to ASCII 0-9 (locale audit, 2026-09-26).
 *
 * Mirror of apps/api/src/common/text/fold-digits.ts.
 * An Arabic keypad types ٠-٩ (U+0660-0669), a Persian one ۰-۹ (U+06F0-06F9),
 * a Hindi InScript keyboard ०-९ (U+0966-096F). JavaScript's `\d` matches none
 * of them, so login codes typed that way were stripped, or compared as 12-byte
 * strings against 6-byte ones, and every correct code failed. Fold before
 * validating or comparing any code, count or phone number.
 *
 * Unicode's stability policy guarantees decimal digits (General_Category Nd)
 * are encoded in contiguous runs of ten, 0 to 9 in order, so a digit's value is
 * its distance from the start of its run, modulo 10 (the mathematical digit
 * runs are several blocks of ten back to back). Each distinct digit is
 * computed once and cached, so folding stays linear in the input.
 */
const DIGIT_VALUE = new Map<string, string>()

export function foldDigits(value: string): string {
  return value.replace(/\p{Nd}/gu, (ch) => {
    if (ch >= '0' && ch <= '9') return ch
    const cached = DIGIT_VALUE.get(ch)
    if (cached !== undefined) return cached
    const cp = ch.codePointAt(0) as number
    let start = cp
    while (start > 0 && /\p{Nd}/u.test(String.fromCodePoint(start - 1))) start--
    const folded = String((cp - start) % 10)
    DIGIT_VALUE.set(ch, folded)
    return folded
  })
}
